import { FC, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {
	useOrganizations,
	useCreateOrganization,
	useUpdateOrganization,
	useDeleteOrganization,
} from "../../hooks/useOrganizations";
import { useGroupLabels } from "../../hooks/groupLabels";
import { useCups } from "../../hooks/useCups";
import { OrganizationResponse } from "../../domain/organization.interface";
import { IOrganizationLevel } from "../../domain/organizationLevel.interface";
import { CupResponse } from "../../domain/cup.interface";
import { useUserStore } from "../../stores/user.store";
import {
	OrganizationRequest,
	OrganizationUpdateRequest,
} from "../../services/organization.service";
import { GroupLabel } from "@/domain/groupLabel.interface";

const OrganizationsComponent: FC = () => {
	const { userInfo } = useUserStore();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingOrganization, setEditingOrganization] =
		useState<OrganizationResponse | null>(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);

	const [formData, setFormData] = useState<
		Omit<OrganizationRequest, "ouLevel">
	>({
		accountId: process.env.REACT_APP_ACCOUNT_ID || "",
		name: "",
		description: "",
		address: {
			address1: "",
			address2: "",
			city: "",
			state: "",
			zipCode: "",
		},
		contact: {
			name: "",
			phoneNumber: "",
		},
		additionalInfo: {
			approvedDate: "",
			npi: "",
		},
		groupLabelIds: [],
		defaultCupId: undefined,
		status: "ACTIVE",
		createdBy: userInfo?.id || "SYSTEM_USER",
	});

	const [, setSelectedOrgLevel] = useState<IOrganizationLevel | null>(null);

	const {
		data: organizationsResponse,
		isLoading: organizationsLoading,
		error: organizationsError,
	} = useOrganizations();

	const { data: groupLabelOptions } = useGroupLabels();
	const { data: cupsResponse } = useCups();

	// Get active cups for the dropdown
	const activeCups = (cupsResponse?.body || []).filter(
		(cup: CupResponse) => cup.status === "ACTIVE",
	);

	const createOrganization = useCreateOrganization();
	const updateOrganization = useUpdateOrganization();
	const deleteOrganization = useDeleteOrganization();

	const organizations = organizationsResponse?.body || [];
	const organizationLevels = [] as IOrganizationLevel[];

	const [selectedOrgLabels, setSelectedOrgLabels] = useState<GroupLabel[]>([]);

	// Filter organizations based on search
	const filteredOrganizations = organizations.filter(
		(org: OrganizationResponse) =>
			org.name.toLowerCase().includes(search.toLowerCase()) ||
			org.description?.toLowerCase().includes(search.toLowerCase()) ||
			"",
	);

	// Paginate organizations
	const paginatedOrganizations = filteredOrganizations.slice(
		page * pageSize,
		(page + 1) * pageSize,
	);

	const handleCreateOrganization = async () => {
		// Validate all required fields
		if (
			!formData.name.trim() ||
			!formData.address.address1.trim() ||
			!formData.address.city.trim() ||
			!formData.address.state.trim() ||
			!formData.address.zipCode.trim() ||
			!formData.contact.name.trim() ||
			!formData.contact.phoneNumber.trim() ||
			!formData.additionalInfo.npi.trim() ||
			!formData.additionalInfo.approvedDate.trim()
		) {
			alert(
				"Please fill in all required fields: Name, Organization Level, Address (Address1, City, State, Zip Code), Contact (Name, Phone), NPI, and Approved Date",
			);
			return;
		}

		try {
			const requestData: OrganizationRequest = {
				...formData,
			};
			await createOrganization.mutateAsync(requestData);
			resetForm();
			setShowCreateForm(false);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error creating organization:", error);
			alert("Failed to create organization");
		}
	};

	const handleUpdateOrganization = async () => {
		// Validate all required fields
		if (
			!editingOrganization ||
			!formData.name.trim() ||
			!formData.address.address1.trim() ||
			!formData.address.city.trim() ||
			!formData.address.state.trim() ||
			!formData.address.zipCode.trim() ||
			!formData.contact.name.trim() ||
			!formData.contact.phoneNumber.trim() ||
			!formData.additionalInfo.npi.trim() ||
			!formData.additionalInfo.approvedDate.trim()
		) {
			alert(
				"Please fill in all required fields: Name, Organization Level, Address (Address1, City, State, Zip Code), Contact (Name, Phone), NPI, and Approved Date",
			);
			return;
		}

		try {
			// Exclude createdBy from update request - only send updatedBy
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { createdBy, ...formDataWithoutCreatedBy } = formData;
			const requestData: OrganizationUpdateRequest = {
				_id: editingOrganization.id,
				...formDataWithoutCreatedBy,
				ouLevel: 0,
				updatedBy: userInfo?.id || "SYSTEM_USER",
			};
			await updateOrganization.mutateAsync(requestData);
			resetForm();
			setEditingOrganization(null);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error updating organization:", error);
			alert("Failed to update organization");
		}
	};

	const handleDeleteOrganization = async (id: string) => {
		if (!confirm("Are you sure you want to delete this organization?")) {
			return;
		}

		try {
			await deleteOrganization.mutateAsync(id);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error deleting organization:", error);
			alert("Failed to delete organization");
		}
	};

	const resetForm = () => {
		setFormData({
			accountId: process.env.REACT_APP_ACCOUNT_ID || "",
			name: "",
			description: "",
			address: {
				address1: "",
				address2: "",
				city: "",
				state: "",
				zipCode: "",
			},
			contact: {
				name: "",
				phoneNumber: "",
			},
			additionalInfo: {
				approvedDate: "",
				npi: "",
			},
			groupLabelIds: [],
			defaultCupId: undefined,
			status: "ACTIVE",
			createdBy: userInfo?.id || "SYSTEM_USER",
		});
		setSelectedOrgLevel(null);
	};

	const handleEditOrganization = (organization: OrganizationResponse) => {
		setEditingOrganization(organization);
		setFormData({
			accountId: process.env.REACT_APP_ACCOUNT_ID || "",
			name: organization.name,
			description: organization.description || "",
			address: {
				address1: organization.address?.address1 || "",
				address2: organization.address?.address2 || "",
				city: organization.address?.city || "",
				state: organization.address?.state || "",
				zipCode: organization.address?.zipCode || "",
			},
			contact: {
				name: organization.contact?.name || "",
				phoneNumber: organization.contact?.phoneNumber || "",
			},
			additionalInfo: {
				approvedDate: organization.additionalInfo?.approvedDate || "",
				npi: organization.additionalInfo?.npi || "",
			},
			groupLabelIds: organization.groupLabelIds || [],
			defaultCupId: organization.defaultCupId || undefined,
			status: organization.status || "ACTIVE",
			createdBy: userInfo?.id || "SYSTEM_USER",
		});

		// Find and set the organization level
		const orgLevel = organizationLevels.find(
			(level: IOrganizationLevel) => level.level === organization.ouLevel,
		);
		setSelectedOrgLevel(orgLevel || null);

		const selectedLabels = (groupLabelOptions || []).filter((gl) =>
			(organization.groupLabelIds || []).includes(gl._id),
		);
		setSelectedOrgLabels(selectedLabels);

		setShowCreateForm(true);
	};

	const handleCancelEdit = () => {
		setEditingOrganization(null);
		setShowCreateForm(false);
		resetForm();
	};

	if (organizationsLoading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="400px"
			>
				<Typography>Loading organizations...</Typography>
			</Box>
		);
	}

	if (organizationsError) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="400px"
			>
				<Typography color="error">
					Error loading organizations: {organizationsError?.message}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ padding: 3 }}>
			<Typography variant="h4" gutterBottom>
				Organizations Management
			</Typography>

			{/* Search and Add Organization */}
			<Card sx={{ marginBottom: 3 }}>
				<CardContent>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Search Organizations"
								variant="outlined"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								InputProps={{
									startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} />,
								}}
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<Box display="flex" justifyContent="flex-end">
								<Button
									variant="contained"
									startIcon={<AddIcon />}
									onClick={() => setShowCreateForm(true)}
								>
									Add Organization
								</Button>
							</Box>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			{/* Create/Edit Organization Form */}
			{showCreateForm && (
				<Card sx={{ marginBottom: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{editingOrganization
								? "Edit Organization"
								: "Create New Organization"}
						</Typography>
						<Grid container spacing={2}>
							{/* Basic Information */}
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Organization Name *"
									variant="outlined"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<Autocomplete
									multiple
									options={groupLabelOptions || []}
									getOptionKey={(option) => option._id}
									getOptionLabel={(option) => option.name}
									value={selectedOrgLabels}
									onChange={(_, newValue) => {
										setSelectedOrgLabels(newValue);
										setFormData({
											...formData,
											groupLabelIds: newValue.map((gl) => gl._id),
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Group Labels"
											placeholder="Select group labels"
										/>
									)}
									disabled={organizationsLoading}
								/>
							</Grid>

							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Description"
									variant="outlined"
									multiline
									rows={3}
									value={formData.description}
									onChange={(e) =>
										setFormData({ ...formData, description: e.target.value })
									}
								/>
							</Grid>

							{/* Address Information */}
							<Grid item xs={12}>
								<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
									Address Information
								</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Address Line 1 *"
									variant="outlined"
									required
									value={formData.address.address1}
									onChange={(e) =>
										setFormData({
											...formData,
											address: {
												...formData.address,
												address1: e.target.value,
											},
										})
									}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Address Line 2"
									variant="outlined"
									value={formData.address.address2}
									onChange={(e) =>
										setFormData({
											...formData,
											address: {
												...formData.address,
												address2: e.target.value,
											},
										})
									}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="City *"
									variant="outlined"
									required
									value={formData.address.city}
									onChange={(e) =>
										setFormData({
											...formData,
											address: { ...formData.address, city: e.target.value },
										})
									}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="State *"
									variant="outlined"
									required
									value={formData.address.state}
									onChange={(e) =>
										setFormData({
											...formData,
											address: { ...formData.address, state: e.target.value },
										})
									}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<TextField
									fullWidth
									label="Zip Code *"
									variant="outlined"
									required
									value={formData.address.zipCode}
									onChange={(e) =>
										setFormData({
											...formData,
											address: { ...formData.address, zipCode: e.target.value },
										})
									}
								/>
							</Grid>

							{/* Contact Information */}
							<Grid item xs={12}>
								<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
									Contact Information
								</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Contact Name *"
									variant="outlined"
									required
									value={formData.contact.name}
									onChange={(e) =>
										setFormData({
											...formData,
											contact: { ...formData.contact, name: e.target.value },
										})
									}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Phone Number *"
									variant="outlined"
									required
									value={formData.contact.phoneNumber}
									onChange={(e) =>
										setFormData({
											...formData,
											contact: {
												...formData.contact,
												phoneNumber: e.target.value,
											},
										})
									}
								/>
							</Grid>

							{/* Additional Information */}
							<Grid item xs={12}>
								<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
									Additional Information
								</Typography>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Approved Date *"
									variant="outlined"
									type="date"
									required
									InputLabelProps={{ shrink: true }}
									value={formData.additionalInfo.approvedDate}
									onChange={(e) =>
										setFormData({
											...formData,
											additionalInfo: {
												...formData.additionalInfo,
												approvedDate: e.target.value,
											},
										})
									}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="NPI *"
									variant="outlined"
									required
									value={formData.additionalInfo.npi}
									onChange={(e) =>
										setFormData({
											...formData,
											additionalInfo: {
												...formData.additionalInfo,
												npi: e.target.value,
											},
										})
									}
								/>
							</Grid>

							{/* Other Fields */}
							<Grid item xs={12} md={6}>
								<FormControl fullWidth>
									<InputLabel>Status</InputLabel>
									<Select
										value={formData.status}
										onChange={(e) =>
											setFormData({ ...formData, status: e.target.value })
										}
									>
										<MenuItem value="ACTIVE">Active</MenuItem>
										<MenuItem value="INACTIVE">Inactive</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={6}>
								<Autocomplete
									options={activeCups}
									getOptionKey={(option) => option._id}
									getOptionLabel={(option) => option.description || option.name}
									value={
										activeCups.find(
											(cup: CupResponse) => cup._id === formData.defaultCupId,
										) || null
									}
									onChange={(_, newValue) => {
										setFormData({
											...formData,
											defaultCupId: newValue?._id || undefined,
										});
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Default Cup"
											placeholder="Select default cup for UDT results"
										/>
									)}
								/>
							</Grid>

							{/* Action Buttons */}
							<Grid item xs={12}>
								<Box
									display="flex"
									gap={2}
									justifyContent="flex-end"
									sx={{ mt: 2 }}
								>
									<Button variant="outlined" onClick={handleCancelEdit}>
										Cancel
									</Button>
									<Button
										variant="contained"
										onClick={
											editingOrganization
												? handleUpdateOrganization
												: handleCreateOrganization
										}
										disabled={
											createOrganization.isPending ||
											updateOrganization.isPending
										}
									>
										{editingOrganization ? "Update" : "Create"}
									</Button>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			)}

			{/* Organizations Table */}
			<Card>
				<CardContent>
					<TableContainer component={Paper}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>Description</TableCell>
									<TableCell>Organization Level</TableCell>
									<TableCell>Address</TableCell>
									<TableCell>Contact</TableCell>
									<TableCell>Status</TableCell>
									<TableCell align="center">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedOrganizations.map(
									(organization: OrganizationResponse) => {
										const orgLevel = organizationLevels.find(
											(level: IOrganizationLevel) =>
												level.level === organization.ouLevel,
										);
										return (
											<TableRow key={organization.id}>
												<TableCell>{organization.name}</TableCell>
												<TableCell>
													{organization.description || "N/A"}
												</TableCell>
												<TableCell>
													{orgLevel ? (
														<Chip
															label={`${orgLevel.name} (Level ${orgLevel.level})`}
															size="small"
															color="primary"
														/>
													) : (
														"N/A"
													)}
												</TableCell>
												<TableCell>
													{organization.address
														? `${organization.address.city}, ${organization.address.state}`
														: "N/A"}
												</TableCell>
												<TableCell>
													{organization.contact?.name || "N/A"}
												</TableCell>
												<TableCell>
													<Chip
														label={organization.status || "ACTIVE"}
														size="small"
														color={
															organization.status === "ACTIVE"
																? "success"
																: "default"
														}
													/>
												</TableCell>
												<TableCell align="center">
													<IconButton
														onClick={() => handleEditOrganization(organization)}
														size="small"
														color="primary"
													>
														<EditIcon />
													</IconButton>
													<IconButton
														onClick={() =>
															handleDeleteOrganization(organization.id)
														}
														size="small"
														color="error"
													>
														<DeleteIcon />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									},
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<TablePagination
						component="div"
						count={filteredOrganizations.length}
						page={page}
						onPageChange={(_, newPage) => setPage(newPage)}
						rowsPerPage={pageSize}
						onRowsPerPageChange={(event) => {
							setPageSize(parseInt(event.target.value, 10));
							setPage(0);
						}}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</CardContent>
			</Card>
		</Box>
	);
};

export default OrganizationsComponent;
